import React, { useCallback, useEffect, useState } from "react";
import { api, loadKategori } from "@/lib/apiClient";
import { AppShell } from "@/components/AppShell";
import { StatsCards } from "@/components/StatsCards";
import { PeriodFilter } from "@/components/PeriodFilter";
import { JobOrderForm } from "@/components/JobOrderForm";
import { JobOrderTable } from "@/components/JobOrderTable";
import { ClientManager } from "@/components/ClientManager";
import { DashboardOverview } from "@/components/DashboardOverview";
import { ExportButtons } from "@/components/ExportButtons";
import { ActivityLog } from "@/components/ActivityLog";
import { UserManager } from "@/components/UserManager";
import { MediaManager } from "@/components/MediaManager";
import { KategoriManager } from "@/components/KategoriManager";
import { MaterialManager } from "@/components/MaterialManager";
import { StockBoard } from "@/components/StockBoard";
import { MaterialBillBoard } from "@/components/MaterialBillBoard";
import { KumendanBoard } from "@/components/KumendanBoard";
import { ProductionBoard } from "@/components/ProductionBoard";
import { QCBoard } from "@/components/QCBoard";
import { AdminReport } from "@/components/AdminReport";
import { AdminBoard } from "@/components/AdminBoard";
import { SalesInvoiceBoard } from "@/components/SalesInvoiceBoard";
import { PaymentBoard } from "@/components/PaymentBoard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard({ role }) {
  const { user } = useAuth();
  const [range, setRange] = useState({ start: "", end: "" });
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [kategoriFilter, setKategoriFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [katVersion, setKatVersion] = useState(0);
  const bumpKategori = useCallback(() => setKatVersion((v) => v + 1), []);

  useEffect(() => {
    loadKategori().then(bumpKategori);
  }, [bumpKategori]);

  const isBoss = role === "manager" || role === "kumendan";
  const canFinance = isBoss || role === "staff";
  const canManage = isBoss || role === "staff";
  const canEntry = isBoss || role === "staff" || role === "cs";
  const isProduksi = role === "produksi";

  const load = useCallback(async () => {
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    const [s, jo] = await Promise.all([
      api.get("/job-orders/stats", { params }),
      api.get("/job-orders", {
        params: { ...params, status: statusFilter, kategori: kategoriFilter, q: search || undefined },
      }),
    ]);
    setStats(s.data);
    setRows(jo.data);
  }, [range, statusFilter, kategoriFilter, search]);

  const loadClients = useCallback(async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const refresh = () => {
    load();
    loadClients();
  };

  return (
    <AppShell>
      <div className="fade-up">
        <p className="uppercase tracking-[0.22em] text-xs font-semibold text-blue-400">
          Dashboard {user?.label} · {user?.username}
        </p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-display text-3xl md:text-4xl font-bold">
            Rekap <span className="metal-text">Job Order</span>
          </h1>
          {isBoss && (
            <ExportButtons
              range={range}
              statusFilter={statusFilter}
              search={search}
              kategoriFilter={kategoriFilter}
            />
          )}
        </div>
      </div>

      <div className="mt-8 space-y-6">
        <PeriodFilter range={range} setRange={setRange} />
        <StatsCards stats={stats} active={statusFilter} onSelect={setStatusFilter} />
      </div>

      <div className="mt-10">
        <Tabs defaultValue={isProduksi ? "produksi" : role === "qc" ? "papan-qc" : isBoss ? "dashboard" : "entri"}>
          <TabsList className="bg-slate-900/70 border border-white/10 rounded-xl p-1 flex-wrap h-auto" data-testid="dashboard-tabs">
            {isBoss && (
              <TabsTrigger data-testid="tab-dashboard" value="dashboard" className="rounded-lg data-[state=active]:bg-teal-600 px-5">
                Dashboard
              </TabsTrigger>
            )}
            {canFinance && (
              <TabsTrigger data-testid="tab-finance" value="finance" className="rounded-lg data-[state=active]:bg-emerald-600 px-5">
                Finance
              </TabsTrigger>
            )}
            {canEntry && (
              <TabsTrigger data-testid="tab-entri" value="entri" className="rounded-lg data-[state=active]:bg-blue-600 px-5">
                Entri Baru
              </TabsTrigger>
            )}
            {canManage && (
              <TabsTrigger data-testid="tab-client" value="client" className="rounded-lg data-[state=active]:bg-blue-600 px-5">
                Data Client
              </TabsTrigger>
            )}
            <TabsTrigger data-testid="tab-produksi" value="produksi" className={`rounded-lg data-[state=active]:bg-blue-600 px-5 ${role === "qc" ? "hidden" : ""}`}>
              Papan Produksi
            </TabsTrigger>
            {(isBoss || role === "qc" || isProduksi || role === "cs") && (
              <TabsTrigger data-testid="tab-papan-qc" value="papan-qc" className="rounded-lg data-[state=active]:bg-blue-600 px-5">
                Papan QC
              </TabsTrigger>
            )}
            <TabsTrigger data-testid="tab-stok-bahan" value="stok-bahan" className="rounded-lg data-[state=active]:bg-teal-600 px-5">
              Stok Bahan
            </TabsTrigger>
            {role === "kumendan" && (
              <TabsTrigger data-testid="tab-papan-kumendan" value="papan-kumendan" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                Papan Kumendan
              </TabsTrigger>
            )}
            {role !== "cs" && (
              <TabsTrigger data-testid="tab-kelola-sistem" value="kelola-sistem" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                Kelola Sistem
              </TabsTrigger>
            )}
            {role === "kumendan" && (
              <TabsTrigger data-testid="tab-akun" value="akun" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                Manajemen Akun
              </TabsTrigger>
            )}
          </TabsList>

          {isBoss && (
            <TabsContent value="dashboard" className="mt-6">
              <DashboardOverview />
            </TabsContent>
          )}

          {canFinance && (
            <TabsContent value="finance" className="mt-6">
              <Tabs defaultValue="laporan-admin">
                <TabsList className="bg-slate-900/70 border border-white/10 rounded-xl p-1 flex-wrap h-auto" data-testid="finance-tabs">
                  {[
                    ["laporan-admin", "Laporan Admin"],
                    ["papan-admin", "Papan Admin"],
                    ["pembayaran", "Papan Pembayaran"],
                    ["faktur-jual", "Faktur Jual"],
                  ].map(([v, label]) => (
                    <TabsTrigger
                      key={v}
                      data-testid={`tab-${v}`}
                      value={v}
                      className="rounded-lg data-[state=active]:bg-emerald-600 px-5"
                    >
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent value="laporan-admin" className="mt-5">
                  <AdminReport />
                </TabsContent>
                <TabsContent value="papan-admin" className="mt-5">
                  <AdminBoard />
                </TabsContent>
                <TabsContent value="pembayaran" className="mt-5">
                  <PaymentBoard />
                </TabsContent>
                <TabsContent value="faktur-jual" className="mt-5">
                  <SalesInvoiceBoard />
                </TabsContent>
              </Tabs>
            </TabsContent>
          )}

          {canEntry && (
            <TabsContent value="entri" className="mt-6">
              <JobOrderForm clients={clients} onSaved={refresh} />
            </TabsContent>
          )}
          {canManage && (
            <TabsContent value="client" className="mt-6">
              <ClientManager clients={clients} onChanged={loadClients} />
            </TabsContent>
          )}
          {(isBoss || role === "qc" || isProduksi || role === "cs") && (
            <TabsContent value="papan-qc" className="mt-6">
              <QCBoard role={role} />
            </TabsContent>
          )}
          <TabsContent value="produksi" className="mt-6">
            <ProductionBoard role={role} />
          </TabsContent>
          <TabsContent value="stok-bahan" className="mt-6">
            <StockBoard role={role} />
          </TabsContent>

          <TabsContent value="kelola-sistem" className="mt-6">
            <Tabs defaultValue={!isProduksi && role !== "qc" ? "daftar" : "aktivitas"}>
              <TabsList className="bg-slate-900/70 border border-white/10 rounded-xl p-1 flex-wrap h-auto" data-testid="kelola-sistem-tabs">
                {!isProduksi && role !== "qc" && role !== "cs" && (
                  <TabsTrigger data-testid="tab-daftar" value="daftar" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                    Daftar JO
                  </TabsTrigger>
                )}
                {canManage && (
                  <TabsTrigger data-testid="tab-tagihan-bahan" value="tagihan-bahan" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                    Tagihan Bahan
                  </TabsTrigger>
                )}
                {canManage && (
                  <TabsTrigger data-testid="tab-kelola-bahan" value="kelola-bahan" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                    Kelola Stok Bahan
                  </TabsTrigger>
                )}
                {role === "kumendan" && (
                  <TabsTrigger data-testid="tab-kategori" value="kategori" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                    Kelola Kategori
                  </TabsTrigger>
                )}
                {role === "kumendan" && (
                  <TabsTrigger data-testid="tab-media" value="media" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                    Media Halaman Awal
                  </TabsTrigger>
                )}
                <TabsTrigger data-testid="tab-aktivitas" value="aktivitas" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 px-5">
                  Aktivitas
                </TabsTrigger>
              </TabsList>

              {!isProduksi && role !== "qc" && (
                <TabsContent value="daftar" className="mt-5">
                  <JobOrderTable
                    rows={rows}
                    role={role}
                    onChanged={load}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    search={search}
                    setSearch={setSearch}
                    kategoriFilter={kategoriFilter}
                    setKategoriFilter={setKategoriFilter}
                  />
                </TabsContent>
              )}
              {canManage && (
                <TabsContent value="tagihan-bahan" className="mt-5">
                  <MaterialBillBoard />
                </TabsContent>
              )}
              {canManage && (
                <TabsContent value="kelola-bahan" className="mt-5">
                  <MaterialManager />
                </TabsContent>
              )}
              {role === "kumendan" && (
                <TabsContent value="kategori" className="mt-5">
                  <KategoriManager onChanged={bumpKategori} />
                </TabsContent>
              )}
              {role === "kumendan" && (
                <TabsContent value="media" className="mt-5">
                  <MediaManager />
                </TabsContent>
              )}
              <TabsContent value="aktivitas" className="mt-5">
                <ActivityLog role={role} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {role === "kumendan" && (
            <TabsContent value="papan-kumendan" className="mt-6">
              <KumendanBoard role={role} />
            </TabsContent>
          )}

          {role === "kumendan" && (
            <TabsContent value="akun" className="mt-6">
              <UserManager />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </AppShell>
  );
}
